import React, { type ReactNode, createContext, useContext, useState } from 'react'

export type ValidationRule = {
  canGoNext?: () => boolean
  canGoPrevious?: () => boolean
}

type StepComponentMap<T extends string> = Record<T, React.ComponentType<any>>

export interface StepManager<T extends string> {
  currentStep: T
  goToNext: () => void
  goToPrevious: () => void
  goToStep: (step: T) => void
  stepInfo: {
    isFirst: boolean
    isLast: boolean
    canGoNext: boolean
    canGoPrevious: boolean
    currentIndex: number
    totalSteps: number
  }
  sharedData: Record<string, any>
  setSharedData: (data: Record<string, any>) => void
  updateSharedData: (key: string, value: any) => void
}

export const StepContext = createContext<StepManager<any> | null>(null)

export const useStepManager = <T extends string>(): StepManager<T> => {
  const context = useContext(StepContext)
  if (!context) {
    throw new Error('useStepManager deve ser usado dentro de um StepRenderer')
  }
  return context
}

export type UseStepRendererProps<T extends string = string> = {
  stepOrder: T[]
  stepComponents: StepComponentMap<T>
  businessValidationRules?: Partial<Record<T, ValidationRule>>
  initialStep?: T
}

export const useStepRenderer = <T extends string = string>({
  stepOrder,
  stepComponents,
  businessValidationRules = {},
  initialStep
}: UseStepRendererProps<T>) => {
  const [currentStep, setCurrentStep] = useState<T>(initialStep || stepOrder[0])
  const [sharedData, setSharedData] = useState<Record<string, any>>({})

  const updateSharedData = (key: string, value: any) => {
    setSharedData(prev => ({ ...prev, [key]: value }))
  }

  const getNextStep = (step: T): T | null => {
    const currentIndex = stepOrder.indexOf(step)
    if (currentIndex === -1 || currentIndex === stepOrder.length - 1) {
      return null
    }
    return stepOrder[currentIndex + 1]
  }

  const getPreviousStep = (step: T): T | null => {
    const currentIndex = stepOrder.indexOf(step)
    if (currentIndex <= 0) {
      return null
    }
    return stepOrder[currentIndex - 1]
  }

  const isFirstStep = (step: T): boolean => step === stepOrder[0]
  const isLastStep = (step: T): boolean => step === stepOrder[stepOrder.length - 1]

  const canGoNext = (step: T): boolean => {
    const structuralCheck = !isLastStep(step)
    const businessCheck = businessValidationRules[step]?.canGoNext?.()
    return !!(structuralCheck && businessCheck)
  }

  const canGoPrevious = (step: T): boolean => {
    const structuralCheck = !isFirstStep(step)
    const businessCheck = businessValidationRules[step]?.canGoPrevious?.()
    return !!(structuralCheck && businessCheck)
  }

  const goToNext = () => {
    if (canGoNext(currentStep)) {
      const nextStep = getNextStep(currentStep)
      if (nextStep) {
        setCurrentStep(nextStep)
      }
    }
  }

  const goToPrevious = () => {
    if (canGoPrevious(currentStep)) {
      const previousStep = getPreviousStep(currentStep)
      if (previousStep) {
        setCurrentStep(previousStep)
      }
    }
  }

  const goToStep = (step: T) => {
    setCurrentStep(step)
  }

  const stepManager: StepManager<T> = {
    currentStep,
    goToNext,
    goToPrevious,
    goToStep,
    stepInfo: {
      isFirst: isFirstStep(currentStep),
      isLast: isLastStep(currentStep),
      canGoNext: canGoNext(currentStep),
      canGoPrevious: canGoPrevious(currentStep),
      currentIndex: stepOrder.indexOf(currentStep),
      totalSteps: stepOrder.length
    },
    sharedData,
    setSharedData,
    updateSharedData
  }

  const renderWithContext = (): ReactNode => {
    const StepComponent = stepComponents[currentStep]
    if (!StepComponent) {
      throw new Error(`Componente não encontrado para o step: ${currentStep}`)
    }

    return (
      <StepContext.Provider value={stepManager}>
        {React.createElement(StepComponent, { stepManager })}
      </StepContext.Provider>
    )
  }

  return {
    stepManager,
    currentStepComponent: renderWithContext()
  }
}
